import React, { useState, useEffect, useMemo } from "react";

interface CustomDatePickerProps {
  id: string;
  label: string;
  value?: string;
  onChange: (value: string | undefined) => void;
  placeholder?: string;
}

const monthNames = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const currentYear = new Date().getFullYear();

// 175 years back from current year, newest first
const years = Array.from({ length: 176 }, (_, i) => currentYear - i);

const getDaysInMonth = (month: number, year: number): number => {
  if (month === 0) return 31;
  const effectiveYear = year === 0 ? 2025 : year;
  return new Date(effectiveYear, month, 0).getDate();
};

const CustomDatePicker = ({
  id,
  label,
  value,
  onChange,
}: CustomDatePickerProps) => {
  // Parse initial value into month/day/year
  const parseValue = (val?: string) => {
    if (!val) return { month: 0, day: 0, year: 0 };
    const date = new Date(`${val}T12:00:00`);
    return {
      month: date.getMonth() + 1, // 1-12
      day: date.getDate(),
      year: date.getFullYear(),
    };
  };

  const initial = parseValue(value);
  const [month, setMonth] = useState(initial.month);
  const [day, setDay] = useState(initial.day);
  const [year, setYear] = useState(initial.year);

  // Sync from parent when value changes externally
  useEffect(() => {
    const parsed = parseValue(value);
    setMonth(parsed.month);
    setDay(parsed.day);
    setYear(parsed.year);
  }, [value]);

  // Compute available days based on selected month/year
  const daysInMonth = useMemo(() => getDaysInMonth(month, year), [month, year]);

  // Clamp day if it exceeds available days (e.g., switching from March 31 to February)
  useEffect(() => {
    if (day > daysInMonth && day !== 0) {
      setDay(daysInMonth);
    }
  }, [daysInMonth, day]);

  // Emit onChange when all three fields are set
  const emitChange = (m: number, d: number, y: number) => {
    if (m > 0 && d > 0 && y > 0) {
      const formatted = `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      onChange(formatted);
    } else if (m === 0 && d === 0 && y === 0) {
      onChange(undefined);
    }
  };

  const handleMonthChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newMonth = parseInt(e.target.value);
    setMonth(newMonth);
    // Clamp day if needed
    const maxDay = getDaysInMonth(newMonth, year);
    const clampedDay = day > maxDay ? maxDay : day;
    if (clampedDay !== day) setDay(clampedDay);
    emitChange(newMonth, clampedDay, year);
  };

  const handleDayChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newDay = parseInt(e.target.value);
    setDay(newDay);
    emitChange(month, newDay, year);
  };

  const handleYearChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newYear = parseInt(e.target.value);
    setYear(newYear);
    // Clamp day if needed (leap year Feb)
    const maxDay = getDaysInMonth(month, newYear);
    const clampedDay = day > maxDay ? maxDay : day;
    if (clampedDay !== day) setDay(clampedDay);
    emitChange(month, clampedDay, newYear);
  };

  const selectBaseClass =
    "h-12 text-base rounded-lg border border-gray-300 bg-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary appearance-none";

  return (
    <div className="space-y-2">
      {label && (
        <label htmlFor={id} className="block text-sm font-medium text-gray-700">
          {label}
        </label>
      )}

      <div className="flex gap-3">
        {/* Month */}
        <div className="flex-1 space-y-1">
          <select
            id={id}
            value={month}
            onChange={handleMonthChange}
            className={`${selectBaseClass} w-full`}
            aria-label="Month"
          >
            <option value={0}>Month</option>
            {monthNames.map((name, i) => (
              <option key={name} value={i + 1}>
                {name}
              </option>
            ))}
          </select>
          <span className="block text-sm text-muted-foreground pl-1">Month</span>
        </div>

        {/* Day */}
        <div className="w-20 space-y-1">
          <select
            value={day}
            onChange={handleDayChange}
            className={`${selectBaseClass} w-full`}
            aria-label="Day"
          >
            <option value={0}>Day</option>
            {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
          <span className="block text-sm text-muted-foreground pl-1">Day</span>
        </div>

        {/* Year */}
        <div className="w-24 space-y-1">
          <select
            value={year}
            onChange={handleYearChange}
            className={`${selectBaseClass} w-full`}
            aria-label="Year"
          >
            <option value={0}>Year</option>
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
          <span className="block text-sm text-muted-foreground pl-1">Year</span>
        </div>
      </div>
    </div>
  );
};

export default CustomDatePicker;
